import { RotatingArrows, DocumentEdit, InfoCircleFilled } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ConfirmModal } from 'common/components';
import { usePluralization } from 'common/hooks';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import {
  OfferOverrides,
  OfferOverridesEntry,
  OfferingApiError,
  SelectionOverridesResponse,
} from 'modules/shared-menu/types';
import { getTypedKeys } from 'modules/shared-menu/utils';

import ActionButton from './ActionButton';
import BulkActionErrorList from './BulkActionErrorList';
import { BulkActionModalTitle } from './BulkActionModalTitle';
import { buildResetOfferOverridesPayloads } from './transformers';
import { useBatchUpdateOfferOverrides } from './useBatchUpdateOfferOverrides';
import { useVenueSelectionOverrides } from '../../../hooks/useSelectionOverrides';

const StyledConfirmModal = styled(ConfirmModal)`
  width: 100%;
  max-width: 480px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${typographyCss.Body3}
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Info = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  ${typographyCss.Small}
`;

const InfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  flex-shrink: 0;
  margin-block-start: 2px;
`;

type OverridableAttrs = Pick<
  OfferOverrides,
  'enabled' | 'weekly_availability' | 'weekly_visibility' | 'price' | 'discounted' | 'merchant_sku' | 'external_id'
>;

const getOverriddenFieldsSummary = (
  selectionOverrides: SelectionOverridesResponse | undefined,
  selectedIds: Set<string>,
): Record<keyof OverridableAttrs, number> => {
  const offerAttrToOverrideCountMap = {} as Record<keyof OfferOverrides, number>;

  selectedIds.forEach(offerId => {
    const { fields_to_reset: fieldsToReset, ...inlineOverrides } = selectionOverrides?.offers?.[offerId] ?? {};
    const overriddenAttributes: (keyof OfferOverrides)[] = [...getTypedKeys(inlineOverrides), ...(fieldsToReset ?? [])];

    overriddenAttributes.forEach(attr => {
      offerAttrToOverrideCountMap[attr] = (offerAttrToOverrideCountMap[attr] ?? 0) + 1;
    });
  });

  return offerAttrToOverrideCountMap;
};

const overridableAttributeToIntlIdMap: Record<keyof OverridableAttrs, string> = {
  enabled: 'menu-item.enabled',
  weekly_availability: 'menu-item.time-available',
  weekly_visibility: 'menu-item.time-visible',
  price: 'menu-item.price',
  discounted: 'menu-item.discount',
  merchant_sku: 'menu.merchant-sku',
  external_id: 'menu.pos-id',
};

type BulkResetOverrides = {
  selectedIds: Set<string>;
};

export const BulkResetOverrides = ({ selectedIds }: BulkResetOverrides) => {
  const intl = useIntl();
  const { formatPluralMessage } = usePluralization();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { offers, selection } = useSelectionData();
  const { executeBatchUpdateOfferOverrides, isLoading } = useBatchUpdateOfferOverrides();
  const { updateSelectionOverridesCache, selectionOverrides } = useVenueSelectionOverrides();

  const overriddenFieldsSummary = getOverriddenFieldsSummary(selectionOverrides, selectedIds);
  const sumOfAllOverriddenAttrs = Object.values(overriddenFieldsSummary).reduce((acc, count) => acc + count, 0);
  const noOverridesInSelectedOffers = sumOfAllOverriddenAttrs === 0;

  const onSuccess = useCallback(
    (successfullyUpdatedOffers: OfferOverridesEntry[]) => {
      toast.success(
        formatPluralMessage({
          id: 'shared-menu.bulk-actions.toasts.update.success-description',
          count: successfullyUpdatedOffers.length,
        }),
      );

      updateSelectionOverridesCache(successfullyUpdatedOffers);
    },
    [formatPluralMessage, updateSelectionOverridesCache],
  );

  const onError = useCallback(
    (errors: OfferingApiError[]) => {
      toast.error(
        <BulkActionErrorList
          allOffers={offers}
          errors={errors}
          selectionPrimaryLanguage={selection.primary_language}
          description={intl.formatMessage({ id: 'shared-menu.bulk-actions.toasts.update.error-description' })}
        />,
        { duration: Infinity },
      );
    },
    [intl, offers, selection.primary_language],
  );

  const handleConfirm = useCallback(async () => {
    const overridesPayloads = buildResetOfferOverridesPayloads(selectedIds);

    await executeBatchUpdateOfferOverrides({
      overridesPayloads,
      selectionId: selection.id,
      onSuccess,
      onError,
    });

    setIsModalOpen(false);
  }, [executeBatchUpdateOfferOverrides, onError, onSuccess, selectedIds, selection.id]);

  return (
    <>
      <ActionButton
        icon={<RotatingArrows />}
        onClick={() => setIsModalOpen(true)}
        tooltipTitle={<FormattedMessage id="listing-manager.bulk-actions.overrides.reset.reset-all" />}
      />
      {isModalOpen && (
        <StyledConfirmModal
          cancelText={<FormattedMessage id="state.cancel" />}
          confirmText={<FormattedMessage id="listing-manager.bulk-actions.overrides.reset.reset-all" />}
          confirmVariant={'lightRed'}
          description={
            <Content>
              {noOverridesInSelectedOffers ? (
                <span>
                  <FormattedMessage id="listing-manager.bulk-actions.overrides.reset.no-overrides" />
                </span>
              ) : (
                <>
                  <span>
                    <FormattedMessage id="listing-manager.bulk-actions.overrides.reset.reset-warning" />
                  </span>
                  <span>
                    <FormattedMessage
                      id="listing-manager.bulk-actions.overrides.reset.change-summary"
                      values={{ strong: (text: string) => <strong>{text}</strong>, sumOfAllOverriddenAttrs }}
                    />
                  </span>
                  <TagRow>
                    {getTypedKeys(overriddenFieldsSummary).map(attr => (
                      <Tag key={attr} size="small" variant="lightBlue" iconPosition="left" icon={<DocumentEdit />}>
                        <FormattedMessage
                          id={overridableAttributeToIntlIdMap[attr] ?? undefined}
                          defaultMessage={attr}
                        />{' '}
                        ({overriddenFieldsSummary[attr]})
                      </Tag>
                    ))}
                  </TagRow>
                  <Info>
                    <InfoIcon />
                    <FormattedMessage id="listing-manager.bulk-actions.overrides.reset.cannot-be-undone-hint" />
                  </Info>
                </>
              )}
            </Content>
          }
          header={
            <BulkActionModalTitle
              count={selectedIds.size}
              title={<FormattedMessage id="listing-manager.bulk-actions.overrides.reset.reset-all" />}
            />
          }
          loading={isLoading}
          onCancel={() => setIsModalOpen(false)}
          onConfirm={handleConfirm}
          disabled={noOverridesInSelectedOffers}
          open
        />
      )}
    </>
  );
};
